'use es6';

import classNames from 'classnames';
import { List } from 'react-virtualized';
import { DROPDOWN_MENU_HEIGHT } from 'HubStyleTokens/sizes';
import { jsx as _jsx } from "react/jsx-runtime";
const MAX_HEIGHT = parseInt(DROPDOWN_MENU_HEIGHT, 10);
const OPTION_HEIGHT = 40;
const OPTION_GROUP_HEIGHT = 40;
const OPTION_WITH_HELP_HEIGHT = 64;
const OPTION_LOADING_HEIGHT = 60;
function isGroup(option) {
  return option && Array.isArray(option.options);
}
function getOptionHeight(option) {
  if (isGroup(option)) {
    return OPTION_GROUP_HEIGHT + option.options.reduce((total, child) => {
      return total + getOptionHeight(child);
    }, 0);
  }
  if (option.help) {
    return OPTION_WITH_HELP_HEIGHT;
  }
  if (option.loading) {
    return OPTION_LOADING_HEIGHT;
  }
  return OPTION_HEIGHT;
}
function calculateListHeight(options) {
  let height = 0;
  for (let optionIndex = 0; optionIndex < options.length; optionIndex++) {
    const option = options[optionIndex];
    height += getOptionHeight(option);
    if (height > MAX_HEIGHT) {
      return MAX_HEIGHT;
    }
  }
  return height;
}
const menuRendererCache = {};
function virtualizedMenuRenderer(menuWidth, virtualizedMenuRendererRefCallback) {
  if (!menuRendererCache[menuWidth]) {
    menuRendererCache[menuWidth] = ({
      focusedOption,
      instancePrefix,
      onFocus,
      onOptionRef,
      onSelect,
      optionClassName,
      optionComponent,
      optionGroupComponent,
      optionRenderer,
      options,
      valueArray,
      valueKey
    }) => {
      const focusedOptionIndex = options.indexOf(focusedOption);
      const height = calculateListHeight(options);
      const Option = optionComponent;
      const OptionGroup = optionGroupComponent;
      const renderLabel = optionRenderer;

      // The styles are generated by react-virtualized to control positioning
      function renderOption(option, i, style) {
        if (isGroup(option)) {
          return /*#__PURE__*/_jsx("span", {
            style: style,
            children: /*#__PURE__*/_jsx(OptionGroup, {
              className: "Select-option-group",
              label: renderLabel(option),
              option: option,
              optionIndex: i,
              children: option.options.map((opt, idx) => renderOption(opt, idx))
            })
          }, `option-group-${i}`);
        }
        const isSelected = valueArray && valueArray.indexOf(option) > -1;
        const isFocused = option === focusedOption;
        const optionClass = classNames("private-typeahead-option--virtualized Select-option", optionClassName, isSelected && 'is-selected', isFocused && 'is-focused', option.disabled && 'is-disabled');
        return /*#__PURE__*/_jsx("span", {
          style: style,
          children: /*#__PURE__*/_jsx(Option, {
            className: optionClass,
            instancePrefix: instancePrefix,
            isDisabled: option.disabled,
            isFocused: isFocused,
            isSelected: isSelected,
            onFocus: onFocus,
            onSelect: onSelect,
            option: option,
            optionIndex: i,
            ref: ref => {
              onOptionRef(ref, isFocused);
            },
            children: renderLabel(option, i)
          })
        }, `option-${i}-${option[valueKey]}`);
      }
      function getRowHeight({
        index
      }) {
        return getOptionHeight(options[index]);
      }
      function renderRow({
        index,
        style
      }) {
        const option = options[index];
        return renderOption(option, index, style);
      }
      return /*#__PURE__*/_jsx(List, {
        height: height,
        rowCount: options.length,
        rowHeight: getRowHeight,
        rowRenderer: renderRow,
        scrollToIndex: focusedOptionIndex,
        width: menuWidth,
        ref: virtualizedMenuRendererRefCallback
      });
    };
  }
  return menuRendererCache[menuWidth];
}
/* eslint-enable react/prop-types */

virtualizedMenuRenderer.displayName = 'virtualizedMenuRenderer';
export default virtualizedMenuRenderer;